/**
 * N2ScorecardLogPage — the full audit log across all four models.
 *
 * Route: /n2/scorecard/log
 *
 * For readers who want to see every reading in one place with the full
 * filter set. Confluence pills still surface member rows.
 */

import React, { useMemo } from 'react';
import { N2Frame } from './N2Frame';
import { Eyebrow, PageTitle, PageLede } from './N2Primitives';
import { unifyRows } from './notchesScorecardService';
import {
  ReadingLog, LoadingPanel, ErrorPanel, ScorecardSubNav,
  MethodologyFootnote, detectConfluence,
} from './notchesScorecardShared';
import { useNotchesScorecard } from './useNotchesScorecard';

export const N2ScorecardLogPage: React.FC = () => {
  const { data, err } = useNotchesScorecard();

  const unified = useMemo(() =>
    data ? unifyRows(data.cot.rows, data.gex.rows, data.iv.rows, data.avwap.rows) : [],
  [data]);

  const confluenceEvents = useMemo(() =>
    unified.length ? detectConfluence(unified) : [],
  [unified]);

  return (
    <N2Frame page="scorecard/log">
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '48px 32px 40px' }}>
        <ScorecardSubNav />

        <div style={{ paddingTop: 24 }}>
          <Eyebrow>Scorecard · Full log</Eyebrow>
          <PageTitle italic="">
            Every reading.
          </PageTitle>
          <PageLede>
            The complete audit trail across COT, GEX, IV, and AVWAP. Filter by
            model or by outcome; every row shows a trade implication under the
            reading. Losing rows are shown in warm brick, not hidden.
          </PageLede>
        </div>

        {err && <ErrorPanel message={err} />}
        {!data && !err && <LoadingPanel />}
        {data && <>
          <ReadingLog rows={unified} confluenceEvents={confluenceEvents} />
          <MethodologyFootnote data={data} />
        </>}
      </div>
    </N2Frame>
  );
};

export default N2ScorecardLogPage;
